import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { CreateDevTelephoneNumberRequestDto, DevTelephoneNumberDto, UpdateDevTelephoneNumberRequestDto } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'

interface DevTelephoneModalProps extends BaseModalProps {
  entry: DevTelephoneNumberDto | null
  onSaved: (entry: DevTelephoneNumberDto) => void
  onDeleted?: (publicId: string) => void
}

const DevTelephoneModal = ({ isOpen, onClose, entry, onSaved, onDeleted }: DevTelephoneModalProps) => {
  const isEdit = entry !== null
  const [telephone, setTelephone] = useState('')
  const [comment, setComment] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setTelephone(entry?.telephone ?? '')
    setComment(entry?.comment ?? '')
    setError(null)
    setIsSaving(false)
    setIsDeleting(false)
  }, [isOpen, entry])

  const handleClose = () => {
    if (isSaving || isDeleting) return
    onClose()
  }

  const canSubmit = telephone.trim().length > 0

  const handleSubmit = async () => {
    const trimmedTelephone = telephone.trim()
    if (!trimmedTelephone) {
      setError('Telephone number is required.')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const payload = {
        telephone: trimmedTelephone,
        comment: comment.trim().length > 0 ? comment.trim() : null,
      }

      const saved = isEdit
        ? await adminService.updateDevTelephone(entry.publicId, payload as UpdateDevTelephoneNumberRequestDto)
        : await adminService.createDevTelephone(payload as CreateDevTelephoneNumberRequestDto)

      onSaved(saved)
      onClose()
    } catch {
      setError(isEdit ? 'Failed to save dev telephone number.' : 'Failed to create dev telephone number.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEdit || !entry || !onDeleted) return
    if (!window.confirm(`Delete dev telephone number ${entry.telephone}?`)) return

    setIsDeleting(true)
    setError(null)
    try {
      await adminService.deleteDevTelephone(entry.publicId)
      onDeleted(entry.publicId)
      onClose()
    } catch {
      setError('Failed to delete dev telephone number.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>{isEdit ? 'Edit dev telephone number' : 'Add dev telephone number'}</h2>
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput
            required
            type="text"
            label="Telephone"
            name="devTelephone"
            value={telephone}
            onChange={setTelephone}
          />
          <TextInput
            type="textArea"
            label="Comment"
            name="devTelephoneComment"
            required={false}
            value={comment}
            onChange={setComment}
          />
        </form>
        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}
        <div className="modal-footer-content">
          {isEdit && onDeleted && (
            <button
              type="button"
              className="styled-button"
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              style={{ marginRight: 'auto' }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving || isDeleting}>
            Cancel
          </button>
          <button
            type="button"
            className="styled-button"
            onClick={handleSubmit}
            disabled={isSaving || isDeleting || !canSubmit}
          >
            {isSaving ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save changes' : 'Add number'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default DevTelephoneModal
